import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User2, Shield, KeyRound, Home, ArrowLeft, ChevronsRight, Camera, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { updateUserProfile } from '../../services/userService';
import commonStyles from '../../styles/common.module.css';
import styles from './UserProfile.module.css';

const initialFormState = {
  first_name: '',
  last_name: '',
  telegram_chat_id: '',
  current_password: '',
  new_password: '',
  confirm_password: '',
};

const UserProfile = ({ embedded = false } = {}) => {
  const { user, loading, setUser } = useAuth();
  const [formData, setFormData] = useState(initialFormState);
  const [isSaving, setIsSaving] = useState(false);
  const [profilePreviewUrl, setProfilePreviewUrl] = useState('');
  const [localPreviewUrl, setLocalPreviewUrl] = useState(null);
  const [profilePhotoFile, setProfilePhotoFile] = useState(null);
  const [removeProfilePhoto, setRemoveProfilePhoto] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const selectedProjectId = localStorage.getItem('selected_project_id');
  const selectedProjectName = localStorage.getItem('selected_project_name');

  useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        telegram_chat_id: user.telegram_chat_id || '',
        current_password: '',
        new_password: '',
        confirm_password: '',
      }));
      if (localPreviewUrl) {
        URL.revokeObjectURL(localPreviewUrl);
        setLocalPreviewUrl(null);
      }
      setProfilePhotoFile(null);
      setRemoveProfilePhoto(false);
      setProfilePreviewUrl(user.profile_photo || '');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [user]);

  useEffect(() => () => {
    if (localPreviewUrl) {
      URL.revokeObjectURL(localPreviewUrl);
    }
  }, [localPreviewUrl]);

  const roles = useMemo(() => user?.roles || [], [user]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhotoChange = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) {
      return;
    }
    const maxSizeBytes = 5 * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      toast.error('Размер файла превышает 5 МБ. Выберите другое изображение.');
      if (event.target) {
        event.target.value = '';
      }
      return;
    }
    if (localPreviewUrl) {
      URL.revokeObjectURL(localPreviewUrl);
    }
    const objectUrl = URL.createObjectURL(file);
    setProfilePhotoFile(file);
    setProfilePreviewUrl(objectUrl);
    setLocalPreviewUrl(objectUrl);
    setRemoveProfilePhoto(false);
  };

  const handleRemovePhoto = () => {
    if (removeProfilePhoto) {
      setRemoveProfilePhoto(false);
      setProfilePreviewUrl(user?.profile_photo || '');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    if (localPreviewUrl) {
      URL.revokeObjectURL(localPreviewUrl);
    }
    setLocalPreviewUrl(null);

    if (profilePhotoFile) {
      setProfilePhotoFile(null);
      setProfilePreviewUrl(user?.profile_photo || '');
      setRemoveProfilePhoto(false);
    } else if (user?.profile_photo) {
      setProfilePreviewUrl('');
      setRemoveProfilePhoto(true);
    } else {
      setProfilePreviewUrl('');
      setRemoveProfilePhoto(false);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getInitials = () => {
    const baseName = (user?.full_name || `${formData.first_name} ${formData.last_name}` || user?.username || 'Пользователь').trim();
    if (!baseName) {
      return 'U';
    }
    const parts = baseName.split(/\s+/).filter(Boolean);
    if (parts.length === 1) {
      return parts[0].charAt(0).toUpperCase();
    }
    return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
  };

  const extractErrorMessage = (error) => {
    if (error.response?.data) {
      const { data } = error.response;
      if (typeof data === 'string') {
        return data;
      }
      if (data.detail) {
        return data.detail;
      }
      if (Array.isArray(data)) {
        return data[0];
      }
      const firstKey = Object.keys(data)[0];
      if (firstKey) {
        const value = data[firstKey];
        if (Array.isArray(value)) {
          return value[0];
        }
        if (typeof value === 'string') {
          return value;
        }
      }
    }
    return 'Не удалось сохранить изменения. Повторите попытку позже.';
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!user) {
      return;
    }

    if (formData.new_password && formData.new_password !== formData.confirm_password) {
      toast.error('Новый пароль и подтверждение не совпадают.');
      return;
    }

    if (formData.new_password && !formData.current_password) {
      toast.error('Для смены пароля укажите текущий пароль.');
      return;
    }

    const formPayload = new FormData();
    formPayload.append('first_name', formData.first_name.trim());
    formPayload.append('last_name', formData.last_name.trim());
    formPayload.append('telegram_chat_id', formData.telegram_chat_id.trim());

    if (profilePhotoFile) {
      formPayload.append('profile_photo', profilePhotoFile);
    }

    if (removeProfilePhoto) {
      formPayload.append('remove_profile_photo', 'true');
    }

    if (formData.new_password) {
      formPayload.append('new_password', formData.new_password);
      formPayload.append('current_password', formData.current_password);
    }

    setIsSaving(true);

    try {
      const { data } = await updateUserProfile(formPayload);
      setUser(data);
      localStorage.setItem('user_full_name', data.full_name || data.username || '');
      localStorage.setItem('user_projects', JSON.stringify(data.projects || []));
      localStorage.setItem('user_profile_photo', data.profile_photo || '');
      setProfilePhotoFile(null);
      setRemoveProfilePhoto(false);
      setProfilePreviewUrl(data.profile_photo || '');
      if (localPreviewUrl) {
        URL.revokeObjectURL(localPreviewUrl);
        setLocalPreviewUrl(null);
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setFormData((prev) => ({
        ...prev,
        first_name: data.first_name || '',
        last_name: data.last_name || '',
        telegram_chat_id: data.telegram_chat_id || '',
        current_password: '',
        new_password: '',
        confirm_password: '',
      }));
      toast.success('Профиль успешно обновлён.');
    } catch (error) {
      toast.error(extractErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={`${commonStyles.pageContainer} ${styles.centered}`}>
        <div className={styles.spinner}></div>
        <p className={styles.loadingText}>Загрузка профиля...</p>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className={`${embedded ? styles.embeddedPage : commonStyles.pageContainer} ${commonStyles.fadeIn} ${styles.profilePage}`}>
      {!embedded && (
        <div className={styles.pageHeader}>
          <div className={styles.titleBlock}>
            <div className={styles.iconWrapper}>
              <User2 size={28} />
            </div>
            <div>
              <h1 className={styles.pageTitle}>Личный кабинет</h1>
              <p className={styles.pageSubtitle}>
                Управляйте персональными данными, паролем и смотрите доступы к проектам.
              </p>
            </div>
          </div>
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={() => navigate('/select-project')}
            >
              <ArrowLeft size={16} />
              <span>К списку проектов</span>
            </button>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={() => navigate('/')}
              disabled={!selectedProjectId}
            >
              <Home size={16} />
              <span>{selectedProjectName ? `В проект «${selectedProjectName}»` : 'Перейти в проект'}</span>
            </button>
          </div>
        </div>
      )}

      <div className={styles.contentGrid}>
        <form className={styles.profileCard} onSubmit={handleSubmit}>
          <div className={styles.avatarSection}>
            <div className={styles.avatarPreview}>
              {profilePreviewUrl ? (
                <img src={profilePreviewUrl} alt="Превью фото профиля" />
              ) : (
                <span className={styles.avatarInitials}>{getInitials()}</span>
              )}
            </div>
            <div className={styles.avatarControls}>
              <p className={styles.avatarTitle}>Фото профиля</p>
              <p className={styles.avatarHint}>Поддерживаются изображения в формате PNG или JPG до 5 МБ.</p>
              <div className={styles.avatarButtons}>
                <label className={styles.uploadButton}>
                  <Camera size={16} />
                  <span>Загрузить фото</span>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                  />
                </label>
                {(profilePreviewUrl || profilePhotoFile || user?.profile_photo) && (
                  <button
                    type="button"
                    className={`${styles.removeButton} ${removeProfilePhoto ? styles.removeButtonActive : ''}`}
                    onClick={handleRemovePhoto}
                  >
                    <Trash2 size={16} />
                    <span>{removeProfilePhoto ? 'Отменить' : 'Удалить'}</span>
                  </button>
                )}
              </div>
              {removeProfilePhoto && (
                <p className={styles.removeNotice}>Фото будет удалено после сохранения изменений.</p>
              )}
            </div>
          </div>

          <h2 className={styles.cardTitle}>Основные данные</h2>
          <div className={styles.formGrid}>
            <label className={styles.field}>
              <span>Имя</span>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                placeholder="Введите имя"
                autoComplete="given-name"
              />
            </label>
            <label className={styles.field}>
              <span>Фамилия</span>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                placeholder="Введите фамилию"
                autoComplete="family-name"
              />
            </label>
            <label className={styles.field}>
              <span>Логин</span>
              <input type="text" value={user.username} disabled />
            </label>
            <label className={styles.field}>
              <span>E-mail</span>
              <input type="email" value={user.email || ''} disabled />
            </label>
            <label className={`${styles.field} ${styles.fullWidth}`}>
              <span>Telegram ID</span>
              <input
                type="text"
                name="telegram_chat_id"
                value={formData.telegram_chat_id}
                onChange={handleChange}
                placeholder="Укажите числовой ID телеграм"
                autoComplete="off"
              />
            </label>
          </div>

          <div className={styles.divider}></div>

          <div className={styles.sectionHeader}>
            <KeyRound size={18} />
            <span>Смена пароля</span>
          </div>
          <p className={styles.helperText}>
            Оставьте поля пустыми, если не хотите обновлять пароль.
          </p>

          <div className={styles.formGrid}>
            <label className={styles.field}>
              <span>Текущий пароль</span>
              <input
                type="password"
                name="current_password"
                value={formData.current_password}
                onChange={handleChange}
                placeholder="Введите текущий пароль"
                autoComplete="current-password"
              />
            </label>
            <label className={styles.field}>
              <span>Новый пароль</span>
              <input
                type="password"
                name="new_password"
                value={formData.new_password}
                onChange={handleChange}
                placeholder="Не менее 8 символов"
                autoComplete="new-password"
              />
            </label>
            <label className={styles.field}>
              <span>Подтвердите пароль</span>
              <input
                type="password"
                name="confirm_password"
                value={formData.confirm_password}
                onChange={handleChange}
                placeholder="Повторите новый пароль"
                autoComplete="new-password"
              />
            </label>
          </div>

          <div className={styles.actionsRow}>
            <button type="submit" className={styles.saveButton} disabled={isSaving}>
              <ChevronsRight size={18} />
              <span>{isSaving ? 'Сохранение...' : 'Сохранить изменения'}</span>
            </button>
          </div>
        </form>

        <div className={styles.infoColumn}>
          <div className={styles.infoCard}>
            <div className={styles.sectionHeader}>
              <Shield size={18} />
              <span>Роли и права</span>
            </div>
            {roles.length === 0 ? (
              <p className={styles.emptyText}>Роли не назначены.</p>
            ) : (
              <div className={styles.badgeGroup}>
                {roles.map((role) => (
                  <span key={role.id || role.name} className={styles.badge}>
                    {role.name}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
